import {
  CheckboxSwitch,
  CircleLoader,
  LoaderSizes,
  StandardPagination,
  brandfolderColorPalette
} from '@brandfolder/react';
import { Trans, t } from '@lingui/macro';
import React, { FunctionComponent, useMemo, useState } from 'react';

import styled from 'styled-components';

import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';

import { PeoplePage, Person } from '@components/people_tags/customPeopleTagTypes';
import { getStandardPaginationLabels } from '@translations';

import { EmptyState } from './EmptyState';
import { useFetchPeopleHubData } from './hooks/useFetchPeopleHubData';
import { PeopleManagementActionsBar } from './PeopleManagementActionsBar';
import { PeopleManagementProvider, usePeopleManagementContext } from './PeopleManagementContext';
import { PeopleManagementFilter } from './PeopleManagementFilter';
import { PeoplePageHeader } from './PeoplePageHeader';
import { PersonCard } from './PersonCard';

const HiddenPeopleEmptyStateWrapper = styled.div`
  margin: 0 auto;
  max-width: 480px;
  padding: 64px 0;
  text-align: center;

  p {
    font-weight: 450;
    font-size: 14px;
    line-height: 20px;
    color: ${brandfolderColorPalette.neutrals[500]};
  }
`;

const PeopleTagsFaceCardsWrapper = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(1, 100%);
  justify-content: center;

  @media (min-width: 320px) {
    grid-template-columns: repeat(2, 48%);
  }
  @media (min-width: 744px) {
    grid-template-columns: repeat(2, minmax(166px, 1fr));
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(166px, 1fr));
  }
  @media (min-width: 1320px) {
    grid-template-columns: repeat(4, minmax(194px, 1fr));
  }
  @media (min-width: 1526px) {
    grid-template-columns: repeat(5, minmax(229px, 1fr));
  }
  @media (min-width: 1728px) {
    grid-template-columns: repeat(6, minmax(15%, 1fr));
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  margin-bottom: 40px;
  padding: 0 20px;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: ${brandfolderColorPalette.neutrals[500]};
  font-weight: 450;
  line-height: 20px;
`;

const BottomPaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
  margin-bottom: 40px;
`;

const ITEMS_PER_PAGE = 24;

// Helper functions for filtering
const isPersonNamed = (person: PeoplePage): boolean =>
  Boolean(person.name && person.name.trim() !== '');

const isPersonVisible = (person: PeoplePage): boolean => !person.isHidden;

interface PaginationBarProps {
  paginationText: string;
  showHiddenPeople: boolean;
  setShowHiddenPeople: (show: boolean) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const PaginationBar: FunctionComponent<PaginationBarProps> = ({
  paginationText,
  showHiddenPeople,
  setShowHiddenPeople,
  currentPage,
  totalPages,
  onPageChange
}) => (
  <PaginationWrapper>
    <PaginationInfo>{paginationText}</PaginationInfo>
    <PaginationControls>
      <CheckboxSwitch
        defaultChecked={showHiddenPeople}
        id="show-hidden-people-toggle"
        label={t`Show hidden people`}
        onChange={(): void => setShowHiddenPeople(!showHiddenPeople)}
      />
      <StandardPagination
        initial={currentPage}
        labels={getStandardPaginationLabels()}
        onPageChange={onPageChange}
        total={totalPages}
      />
    </PaginationControls>
  </PaginationWrapper>
);

interface PeopleManagementContentProps {
  initialLoadingState: boolean;
  peopleData: PeoplePage[];
  peopleDataLoading: boolean;
  bfProcessed: boolean;
  allPeopleData: PeoplePage[];
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalCount: number;
  onUpdateSuccess?: () => void | Promise<void>;
  showHiddenPeople: boolean;
  setShowHiddenPeople: (show: boolean) => void;
}

const PeopleManagementContent: FunctionComponent<PeopleManagementContentProps> = ({
  initialLoadingState,
  peopleData,
  peopleDataLoading,
  bfProcessed,
  allPeopleData,
  totalPages,
  currentPage,
  onPageChange,
  totalCount,
  onUpdateSuccess = undefined,
  showHiddenPeople,
  setShowHiddenPeople
}) => {
  const showPageLoader = initialLoadingState || peopleDataLoading;

  // Filter people data based on showHiddenPeople toggle
  const filteredPeopleData = useMemo(() => {
    return showHiddenPeople
      ? peopleData
      : peopleData.filter(isPersonVisible);
  }, [peopleData, showHiddenPeople]);

  // Check if there are any people at all (including hidden)
  const hasAnyPeople = allPeopleData?.length > 0;

  // Check if all people on the current page are hidden
  const allPeopleHiddenOnPage = peopleData?.length > 0 && filteredPeopleData?.length === 0;

  // Show empty state only when no people exist at all
  const showNoFacesEmptyState = !hasAnyPeople && !showPageLoader;

  // Transform PeoplePage[] to Person[] for autocomplete
  const availablePeople = useMemo<Person[]>(() => {
    return allPeopleData
      .filter(isPersonNamed)
      .map((person) => ({
        person_id: person.person_id,
        tag_name: person.name,
        face_uri: person.thumbnail_url,
        face_count: person.face_count,
      }));
  }, [allPeopleData]);

  if (showPageLoader) {
    return <CircleLoader label="people-data" size={LoaderSizes.Large} />;
  }

  if (showNoFacesEmptyState) {
    return <EmptyState bfProcessed={bfProcessed} />;
  }

  // Show message when faces exist but all are hidden and toggle is off
  if (allPeopleHiddenOnPage && !showHiddenPeople) {
    return (
      <>
        <PaginationBar
          currentPage={currentPage}
          onPageChange={onPageChange}
          paginationText={t`0 people`}
          setShowHiddenPeople={setShowHiddenPeople}
          showHiddenPeople={showHiddenPeople}
          totalPages={totalPages}
        />
        <HiddenPeopleEmptyStateWrapper>
          <p>
            <Trans>
              Faces have been detected, but they&apos;re currently hidden.
              Turn on <strong>Show hidden people</strong> to view and manage these identities.
            </Trans>
          </p>
        </HiddenPeopleEmptyStateWrapper>
      </>
    );
  }

  // Calculate pagination info based on filtered (visible) data
  const displayCount = filteredPeopleData.length;
  const startItem = displayCount > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const endItem = displayCount > 0 ? startItem + displayCount - 1 : 0;

  const paginationText = displayCount > 0
    ? t`${startItem}–${endItem} of ${totalCount} ${totalCount === 1 ? 'person' : 'people'}`
    : t`0 people`;

  return (
    <>
      <PaginationBar
        currentPage={currentPage}
        onPageChange={onPageChange}
        paginationText={paginationText}
        setShowHiddenPeople={setShowHiddenPeople}
        showHiddenPeople={showHiddenPeople}
        totalPages={totalPages}
      />
      <PeopleTagsFaceCardsWrapper>
        {filteredPeopleData.map((faceCard) => (
          <PersonCard
            key={faceCard.person_id}
            availablePeople={availablePeople}
            faceCount={faceCard.face_count}
            hidden={faceCard.isHidden}
            imageUrl={faceCard.thumbnail_url}
            name={faceCard.name}
            onUpdateSuccess={onUpdateSuccess ? (): void => { void onUpdateSuccess(); } : undefined}
            personId={faceCard.person_id}
          />
        ))}
      </PeopleTagsFaceCardsWrapper>
      <BottomPaginationWrapper>
        <StandardPagination
          initial={currentPage}
          labels={getStandardPaginationLabels()}
          onPageChange={onPageChange}
          total={totalPages}
        />
      </BottomPaginationWrapper>
    </>
  );
};

export const PeopleManagement: FunctionComponent = () => {
  const { updateFilter, updatePage, state } = usePeopleManagementContext();
  const [showHiddenPeople, setShowHiddenPeople] = useState(true);
  const {
    initialLoadingState,
    peopleData,
    allPeopleData,
    peopleDataLoading,
    allCount,
    namedCount,
    unnamedCount,
    bfProcessed,
    totalPages,
    currentPage,
    refetchPeopleData,
  } = useFetchPeopleHubData();

  // Determine total count based on active filter and showHiddenPeople toggle
  const totalCount = useMemo(() => {
    // First, determine base data: all people or only visible (non-hidden) people
    const baseData = showHiddenPeople
      ? allPeopleData
      : allPeopleData.filter(isPersonVisible);

    // Then apply the active filter
    switch (state.filter) {
      case 'named':
        return baseData.filter(isPersonNamed).length;
      case 'unnamed':
        return baseData.filter((person) => !isPersonNamed(person)).length;
      case 'all':
      default:
        return baseData.length;
    }
  }, [state.filter, showHiddenPeople, allPeopleData]);

  return (
    <I18nProviderWrapper>
      <div className="people-management-wrapper">
        <PeoplePageHeader />
        <PeopleManagementActionsBar />
        <PeopleManagementFilter
          activeFilter={state.filter}
          allCount={allCount}
          namedCount={namedCount}
          onFilterChange={updateFilter}
          unnamedCount={unnamedCount}
        />
        <PeopleManagementContent
          allPeopleData={allPeopleData}
          bfProcessed={bfProcessed}
          currentPage={currentPage}
          initialLoadingState={initialLoadingState}
          onPageChange={updatePage}
          onUpdateSuccess={refetchPeopleData}
          peopleData={peopleData}
          peopleDataLoading={peopleDataLoading}
          setShowHiddenPeople={setShowHiddenPeople}
          showHiddenPeople={showHiddenPeople}
          totalCount={totalCount}
          totalPages={totalPages}
        />
      </div>
    </I18nProviderWrapper>
  );
};

export default (): JSX.Element => {
  return (
    <PeopleManagementProvider>
      <PeopleManagement />
    </PeopleManagementProvider>
  );
};
